import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGO, CONTACT } from "@/data/content";

export default function Navbar({ onNavigate, onPlan }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => onNavigate(id), open ? 250 : 0);
  };

  const dark = !scrolled && !open;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
        scrolled && !open ? "bg-ivory/85 backdrop-blur-md border-b border-gold/20" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-10">
        <button
          data-testid="nav-home-link"
          onClick={() => go("home")}
          className="flex items-center gap-3"
          aria-label="Mirevaa Journeys home"
        >
          <img src={LOGO} alt="Mirevaa Journeys logo" className="h-11 w-11 rounded-full object-cover" />
          <span className="text-left leading-none">
            <span
              className={`block font-display text-xl tracking-[0.18em] transition-colors duration-500 ${
                open ? "text-ivory" : dark ? "text-ivory" : "text-forest"
              }`}
            >
              MIREVAA
            </span>
            <span
              className={`mt-1 block text-[9px] uppercase tracking-widest2 transition-colors duration-500 ${
                open ? "text-gold" : dark ? "text-gold" : "text-forest-mist"
              }`}
            >
              Journeys
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`gold-link text-[11px] uppercase tracking-[0.2em] font-medium transition-colors duration-500 ${
                open ? "text-ivory/90" : dark ? "text-ivory/90" : "text-forest/80"
              } hover:text-gold`}
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta-button"
            onClick={() => {
              setOpen(false);
              onPlan();
            }}
            className={`border px-7 py-3 text-[11px] uppercase tracking-widest2 font-medium transition-all duration-500 ${
              open || dark
                ? "border-gold/70 text-gold hover:bg-gold hover:text-forest"
                : "border-forest/30 text-forest hover:bg-forest hover:text-ivory"
            }`}
          >
            Plan Your Journey
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="h-6 w-6 text-ivory" /> : <Menu className={`h-6 w-6 ${dark ? "text-ivory" : "text-forest"}`} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 -z-10 flex h-screen flex-col justify-center bg-forest lg:hidden"
          >
            <div className="flex flex-col gap-2 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-link-${l.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => go(l.id)}
                  className="border-b border-ivory/10 py-5 text-left font-display text-3xl text-ivory"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-cta-button"
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => {
                  setOpen(false);
                  onPlan();
                }}
                className="mt-8 bg-gold py-4 text-center text-xs uppercase tracking-widest2 font-medium text-forest"
              >
                Start the Conversation
              </motion.button>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.6 }}
                transition={{ delay: 0.65 }}
                className="mt-10 text-center text-xs tracking-widest text-ivory/70"
              >
                {CONTACT.email}
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
